/**
 * Time off (D-031): a stretch when a cleaner is away — vacation, out of town,
 * sick, or just not available. From one day to another, all day or only some
 * hours of each day, with an optional reason and a note. The cleaner picker
 * and Today treat them as unavailable then, and the Schedule marks their
 * visits as needing cover. Pure.
 */
import { z } from 'zod'
import { recordIdSchema } from '../shared/record-id.ts'
import { ASSUMED_VISIT_HOURS } from '../shared/visit.ts'
import { addDays, clockOf, shortDayOf, wallClock, zonedInstant } from '../shared/zoned.ts'

export const timeOffReasons = ['vacation', 'out_of_town', 'sick', 'other'] as const
export type TimeOffReason = (typeof timeOffReasons)[number]

export const timeOffReasonLabels: Record<TimeOffReason, string> = {
  vacation: 'Vacation',
  out_of_town: 'Out of town',
  sick: 'Sick',
  other: 'Other',
}

const dayText = z.iso.date()
const clock = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'A time like 13:00.')

/** What a person fills in. */
export const timeOffFormSchema = z
  .object({
    /** First day, 'YYYY-MM-DD', on the business's calendar. */
    startDate: dayText,
    /** Last day, inclusive. The same as the first for one day. */
    endDate: dayText,
    /** All day, every day in the range; otherwise only `startTime`–`endTime` of each day. */
    allDay: z.boolean().default(true),
    startTime: clock.nullable().default(null),
    endTime: clock.nullable().default(null),
    reason: z.enum(timeOffReasons).nullable().default(null),
    note: z.string().trim().max(500).default(''),
  })
  .superRefine((value, ctx) => {
    if (value.endDate < value.startDate) {
      ctx.addIssue({
        code: 'custom',
        path: ['endDate'],
        message: 'The last day can’t be before the first.',
      })
    }
    if (value.allDay) return
    if (!value.startTime || !value.endTime) {
      ctx.addIssue({
        code: 'custom',
        path: ['startTime'],
        message: 'Pick the hours they are away, or choose all day.',
      })
    } else if (value.endTime <= value.startTime) {
      ctx.addIssue({
        code: 'custom',
        path: ['endTime'],
        message: 'The end has to be after the start.',
      })
    }
  })
export type TimeOffForm = z.infer<typeof timeOffFormSchema>

/** A period as it is kept. All-day periods keep no hours. */
export const timeOffSchema = z.object({
  id: recordIdSchema,
  cleanerId: recordIdSchema,
  startDate: dayText,
  endDate: dayText,
  allDay: z.boolean(),
  startTime: clock.nullable(),
  endTime: clock.nullable(),
  reason: z.enum(timeOffReasons).nullable(),
  note: z.string().max(500),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  /** Removed periods are kept, so Undo and the activity log can bring them back. */
  deletedAt: z.iso.datetime().nullable().default(null),
})
export type TimeOff = z.infer<typeof timeOffSchema>

/** The stored fields for a form: an all-day period keeps no hours. */
export function timeOffFields(form: TimeOffForm) {
  return {
    startDate: form.startDate,
    endDate: form.endDate,
    allDay: form.allDay,
    startTime: form.allDay ? null : form.startTime,
    endTime: form.allDay ? null : form.endTime,
    reason: form.reason,
    note: form.note,
  }
}

type Span = Pick<TimeOff, 'startDate' | 'endDate' | 'allDay' | 'startTime' | 'endTime'>

/** The stretches of the clock a period takes — one per day in it. */
function dayWindows(period: Span, timeZone: string): [number, number][] {
  const windows: [number, number][] = []
  for (let day = period.startDate; day <= period.endDate; day = addDays(day, 1)) {
    const from = zonedInstant(
      `${day}T${period.allDay ? '00:00' : (period.startTime ?? '00:00')}`,
      timeZone,
    )
    const to = period.allDay
      ? zonedInstant(`${addDays(day, 1)}T00:00`, timeZone)
      : zonedInstant(`${day}T${period.endTime ?? '23:59'}`, timeZone)
    windows.push([Date.parse(from), Date.parse(to)])
    if (windows.length > 400) break
  }
  return windows
}

/** The period that takes any of `start` to `start` + `hours`, if there is one. */
export function timeOffClash(
  periods: readonly TimeOff[],
  slot: { start: string; hours: number | null },
  timeZone: string,
): TimeOff | undefined {
  const from = Date.parse(slot.start)
  const to = from + (slot.hours ?? ASSUMED_VISIT_HOURS) * 3_600_000
  return periods.find(
    (period) =>
      period.deletedAt === null &&
      dayWindows(period, timeZone).some(([a, b]) => a < to && b > from),
  )
}

export type TimeOffStatus = 'current' | 'upcoming' | 'past'

/** Where a period stands today: over, going on (today is in it), or still ahead. */
export function timeOffStatus(period: Span, today: string): TimeOffStatus {
  if (period.endDate < today) return 'past'
  return period.startDate <= today ? 'current' : 'upcoming'
}

/** "Oct 30", "Oct 30 – Nov 3". */
export function timeOffDates(period: Span, year?: number): string {
  const label = (day: string) =>
    `${shortDayOf(day)}${year !== undefined && Number(day.slice(0, 4)) !== year ? `, ${day.slice(0, 4)}` : ''}`
  return period.startDate === period.endDate
    ? label(period.startDate)
    : `${label(period.startDate)} – ${label(period.endDate)}`
}

/** "All day", "1:00 PM – 5:00 PM", "1:00 PM – 5:00 PM each day". */
export function timeOffHours(period: Span): string {
  if (period.allDay || !period.startTime || !period.endTime) return 'All day'
  const range = `${clockOf(period.startTime)} – ${clockOf(period.endTime)}`
  return period.startDate === period.endDate ? range : `${range} each day`
}

export interface TimeOffEntry {
  id: string
  startDate: string
  endDate: string
  allDay: boolean
  startTime: string | null
  endTime: string | null
  reason: TimeOffReason | null
  note: string
  status: TimeOffStatus
  /** "Oct 30 – Nov 3" */
  datesText: string
  /** "All day", "1:00 PM – 5:00 PM each day" */
  hoursText: string
  /** "Vacation", or null. */
  reasonText: string | null
}

export function timeOffEntry(period: TimeOff, now: string, timeZone: string): TimeOffEntry {
  const w = wallClock(now, timeZone)
  const today = `${w.year}-${String(w.month).padStart(2, '0')}-${String(w.day).padStart(2, '0')}`
  return {
    id: period.id,
    startDate: period.startDate,
    endDate: period.endDate,
    allDay: period.allDay,
    startTime: period.startTime,
    endTime: period.endTime,
    reason: period.reason,
    note: period.note,
    status: timeOffStatus(period, today),
    datesText: timeOffDates(period, w.year),
    hoursText: timeOffHours(period),
    reasonText: period.reason ? timeOffReasonLabels[period.reason] : null,
  }
}

/**
 * The header's badge while a period is on: "Away until Nov 3" for a whole day,
 * or the hours for a part of one. Null when they are not away right now.
 */
export function awayNow(periods: readonly TimeOff[], now: string, timeZone: string): string | null {
  const at = Date.parse(now)
  const w = wallClock(now, timeZone)
  const active = periods
    .filter(
      (p) => p.deletedAt === null && dayWindows(p, timeZone).some(([a, b]) => a <= at && at < b),
    )
    .sort((a, b) => b.endDate.localeCompare(a.endDate))[0]
  if (!active) return null
  if (!active.allDay) return `Away ${timeOffHours({ ...active, endDate: active.startDate })} today`
  return `Away until ${timeOffDates({ ...active, startDate: active.endDate }, w.year)}`
}

/**
 * What the picker and the job page say beside a cleaner who is away during a
 * visit: "Away until Nov 3", or the hours for part of a day. Real dates from
 * the period, formatted like every other date.
 */
export function awayText(period: TimeOff, year?: number): string {
  if (!period.allDay) return `Away ${timeOffHours(period)}, ${timeOffDates(period, year)}`
  return `Away until ${timeOffDates({ ...period, startDate: period.endDate }, year)}`
}

/** The periods on a business day, for the picker's reason. */
export function timeOffText(period: TimeOff, year?: number): string {
  const reason = period.reason ? ` (${timeOffReasonLabels[period.reason]})` : ''
  const hours = period.allDay ? '' : `, ${timeOffHours(period)}`
  return `${timeOffDates(period, year)}${hours}${reason}`
}
